// MOMERY CUSTOM — Printess pricing preview integration.
(function () {
    if (window.__customPrintessPricingPreviewInit) return;
    window.__customPrintessPricingPreviewInit = true;

    const REGISTER_RETRY_INTERVAL_MS = 250;
    const REGISTER_TIMEOUT_MS = 10000;
    const POLL_MS = 700;
    const DEBOUNCE_MS = 400;
    const TOKEN_EXPIRY_BUFFER_SECONDS = 30;
    const MAX_MEMOIZED_RESPONSES = 50;
    const MEMOIZED_RESPONSE_TTL_MS = 60 * 1000;
    const RETRY_BACKOFF_INITIAL_MS = 2 * 1000;
    const RETRY_BACKOFF_MAX_MS = 30 * 1000;
    const DEBUG_MODE = false;
    const PREVIEW_TOKEN_URL = '/apps/fac-pricing/pricing/preview-token';
    const PRICING_PREVIEW_URL = '/apps/fac-pricing/pricing/preview';

    let currentApi = null;
    let currentContext = null;
    let pollingInterval = null;
    let debounceTimer = null;
    let lastSuccessfulPayloadSignature = null;
    let inFlightPayloadSignature = null;
    let activePreviewAbortController = null;
    let requestSequence = 0;
    let tokenGeneration = 0;
    let lastUnavailableMessage = null;
    let retryState = null;

    const tokenCache = new Map();
    const tokenRequestCache = new Map();
    const previewResponseCache = new Map();

    function registerPlugin() {
        if (window.__customPrintessPricingPreviewPluginRegistered) return true;
        if (typeof window.registerPrintessPlugin !== 'function') return false;

        window.registerPrintessPlugin({
            onOpenAsync(context) {
                const api = context?.integration?.getApi?.() || null;

                if (!api || typeof api.getAllPriceRelevantFormFieldsExt !== 'function') {
                    console.warn('[CUSTOM_PRINTESS_PRICING] Required Printess pricing API is not available');
                    return Promise.resolve();
                }

                currentApi = api;
                currentContext = context;
                lastSuccessfulPayloadSignature = null;
                inFlightPayloadSignature = null;
                lastUnavailableMessage = null;
                resetRetryState();

                schedulePricingSync();
                startPolling();

                return Promise.resolve();
            },

            onCloseAsync() {
                cleanupPricingPreviewRuntime();
                return Promise.resolve();
            },

            async getPricingDisplayValuesAsync(context, currentDisplay, priceInfo) {
                try {
                    const payload = buildWizardPricingPreviewPayload(context, priceInfo);
                    const pricingResult = await resolvePricingPreview(payload);

                    return createPricingDisplay(currentDisplay, pricingResult?.response);
                } catch (error) {
                    debugLog('Wizard pricing preview is unavailable', error);
                    return currentDisplay;
                }
            },
        });

        window.__customPrintessPricingPreviewPluginRegistered = true;
        return true;
    }

    function waitForPluginRegistration() {
        if (registerPlugin()) return;

        const startedAt = Date.now();
        const retryTimer = window.setInterval(() => {
            if (registerPlugin()) {
                window.clearInterval(retryTimer);
                return;
            }

            if (Date.now() - startedAt >= REGISTER_TIMEOUT_MS) {
                window.clearInterval(retryTimer);
                console.warn('[CUSTOM_PRINTESS_PRICING] Printess plugin registration is not available');
            }
        }, REGISTER_RETRY_INTERVAL_MS);
    }

    waitForPluginRegistration();

    function startPolling() {
        stopPolling();
        pollingInterval = window.setInterval(schedulePricingSync, POLL_MS);
    }

    function stopPolling() {
        if (pollingInterval) {
            window.clearInterval(pollingInterval);
            pollingInterval = null;
        }
    }

    function schedulePricingSync() {
        if (debounceTimer) {
            window.clearTimeout(debounceTimer);
        }

        debounceTimer = window.setTimeout(() => {
            debounceTimer = null;
            void syncPricingPayload();
        }, DEBOUNCE_MS);
    }

    function cancelStaleInFlightRequest(payloadSignature) {
        if (inFlightPayloadSignature && inFlightPayloadSignature !== payloadSignature) {
            requestSequence += 1;
            abortActivePreviewRequest();
            inFlightPayloadSignature = null;
        }
    }

    function cancelActiveInFlightRequest() {
        if (inFlightPayloadSignature) {
            requestSequence += 1;
            abortActivePreviewRequest();
            inFlightPayloadSignature = null;
        }
    }

    async function syncPricingPayload() {
        if (!currentApi || !currentContext) {
            return;
        }

        let payload;
        let payloadSignature;

        try {
            payload = buildPricingPreviewPayload(currentContext, currentApi);
            payloadSignature = createStablePayloadSignature(payload);
        } catch (error) {
            cancelActiveInFlightRequest();
            handlePricingUnavailable(error);
            return;
        }

        if (payloadSignature === lastSuccessfulPayloadSignature) {
            return;
        }

        try {
            const pricingResult = await resolvePricingPreview(payload, {
                onRequestStart: () => handlePricingLoading(payload),
            });

            if (!pricingResult) {
                return;
            }

            lastSuccessfulPayloadSignature = payloadSignature;
            handlePricingSuccess(
                pricingResult.response,
                payloadSignature,
                pricingResult.fromCache
            );
        } catch (error) {
            handlePricingError(error);
        }
    }

    async function resolvePricingPreview(payload, options = {}) {
        const payloadSignature = createStablePayloadSignature(payload);

        cancelStaleInFlightRequest(payloadSignature);
        resetRetryStateForPayloadChange(payloadSignature);

        if (payloadSignature === inFlightPayloadSignature) {
            return null;
        }

        const cachedPreviewResponse = getMemoizedPreviewResponse(payloadSignature);

        if (cachedPreviewResponse) {
            return {
                response: cachedPreviewResponse,
                payloadSignature,
                fromCache: true,
            };
        }

        if (!isPricingRequestAllowed(payloadSignature)) {
            return null;
        }

        const currentRequestSequence = ++requestSequence;
        const previewAbortController = new AbortController();

        abortActivePreviewRequest();

        activePreviewAbortController = previewAbortController;
        inFlightPayloadSignature = payloadSignature;

        try {
            if (typeof options.onRequestStart === 'function') {
                options.onRequestStart();
            }

            const token = await getPreviewToken(payload.printess_template_name);
            const pricingResponse = await requestPricingPreview(
                payload,
                token,
                previewAbortController.signal
            );

            if (currentRequestSequence !== requestSequence) {
                return null;
            }

            return completePricingPreviewResolution(
                payloadSignature,
                pricingResponse,
                false
            );
        } catch (error) {
            if (isAbortError(error) || currentRequestSequence !== requestSequence) {
                return null;
            }

            if (error instanceof PricingRequestError && error.status === 401) {
                return await retryPricingPreviewOnce(
                    payload,
                    payloadSignature,
                    currentRequestSequence
                );
            }

            registerPricingFailure(payloadSignature, error);
            throw error;
        } finally {
            if (activePreviewAbortController === previewAbortController) {
                activePreviewAbortController = null;
            }

            if (inFlightPayloadSignature === payloadSignature) {
                inFlightPayloadSignature = null;
            }
        }
    }

    async function retryPricingPreviewOnce(payload, payloadSignature, originalRequestSequence) {
        const retryAbortController = new AbortController();

        abortActivePreviewRequest();

        activePreviewAbortController = retryAbortController;
        inFlightPayloadSignature = payloadSignature;

        try {
            invalidateToken(payload.printess_template_name);

            const token = await getPreviewToken(payload.printess_template_name);

            const pricingResponse = await requestPricingPreview(
                payload,
                token,
                retryAbortController.signal
            );

            if (originalRequestSequence !== requestSequence) {
                return null;
            }

            return completePricingPreviewResolution(payloadSignature, pricingResponse, false);
        } catch (retryError) {
            if (isAbortError(retryError)) {
                return null;
            }

            if (originalRequestSequence !== requestSequence) {
                return null;
            }

            registerPricingFailure(payloadSignature, retryError);
            throw retryError;
        } finally {
            if (activePreviewAbortController === retryAbortController) {
                activePreviewAbortController = null;
            }

            if (inFlightPayloadSignature === payloadSignature) {
                inFlightPayloadSignature = null;
            }
        }
    }

    function completePricingPreviewResolution(payloadSignature, pricingResponse, fromCache) {
        resetRetryState();
        memoizePreviewResponse(payloadSignature, pricingResponse);

        return {
            response: cloneValue(pricingResponse),
            payloadSignature,
            fromCache,
        };
    }

    function buildPricingPreviewPayload(context, api) {
        const templateName = readTemplateName(context);
        const selectedOptions = readSelectedOptions(api);
        const pageCount = readBookPageCount(api);

        debugLog('Resolved pricing payload data', {
            templateName,
            selectedOptions,
            pageCount,
        });

        return createPricingPreviewPayload(templateName, selectedOptions, pageCount);
    }

    function buildWizardPricingPreviewPayload(context, priceInfo) {
        const templateName = readTemplateName(context);
        const wizardFields = priceInfo?.priceRelevantFormFields;
        const selectedOptions = normalizePriceRelevantFields(
            wizardFields?.formFields ? wizardFields : { formFields: wizardFields }
        );
        const pageCount = Number(priceInfo?.pageCount);

        debugLog('Resolved wizard pricing payload data', {
            templateName,
            selectedOptions,
            pageCount,
        });

        return createPricingPreviewPayload(templateName, selectedOptions, pageCount);
    }

    function createPricingPreviewPayload(templateName, selectedOptions, pageCount) {
        if (!templateName) {
            throw new Error('Missing Printess template name');
        }

        if (!selectedOptions || !Object.keys(selectedOptions).length) {
            throw new Error('No selected pricing options found');
        }

        const payload = {
            printess_template_name: templateName,
            selected_options: selectedOptions,
        };

        if (Number.isInteger(pageCount) && pageCount > 0) {
            payload.page_count = pageCount;
        }

        return payload;
    }

    function createPricingDisplay(currentDisplay, pricingResponse) {
        const totalDeltaCents = Number(pricingResponse?.total_delta_cents);

        if (!Number.isInteger(totalDeltaCents) || totalDeltaCents <= 0) {
            return currentDisplay;
        }

        const formattedDelta = formatPricingDelta(totalDeltaCents);
        const surchargeText = `+€${formattedDelta} EUR Seitenaufpreis`;
        const existingLegalNotice = typeof currentDisplay?.legalNotice === 'string'
            ? currentDisplay.legalNotice.trim()
            : '';

        return {
            ...(currentDisplay || {}),
            legalNotice: existingLegalNotice
                ? `${surchargeText} · ${existingLegalNotice}`
                : surchargeText,
        };
    }

    function formatPricingDelta(cents) {
        return new Intl.NumberFormat('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(cents / 100);
    }

    function readSelectedOptions(api) {
        return window.FacPrintessPricingFields?.readSelectedOptions(api) || {};
    }

    function readBookPageCount(api) {
        const bookDocument = readBookDocument(api);
        const pageCount = Number(bookDocument?.pageCount);

        debugLog('Resolved Printess book document', bookDocument);

        if (!Number.isInteger(pageCount) || pageCount <= 0) {
            return null;
        }

        return pageCount;
    }

    function readBookDocument(api) {
        const docsAndSpreads = safeApiCall(api, 'getAllDocsAndSpreads', [true]);

        debugLog('Printess docs and spreads response', docsAndSpreads);

        if (!Array.isArray(docsAndSpreads)) {
            return null;
        }

        return docsAndSpreads.find((item) => item?.isBook === true) || null;
    }

    async function getPreviewToken(templateName) {
        const cachedToken = tokenCache.get(templateName);

        if (cachedToken && !isTokenExpired(cachedToken)) {
            return cachedToken.token;
        }

        const pendingTokenRequest = tokenRequestCache.get(templateName);

        if (pendingTokenRequest) {
            return pendingTokenRequest.promise;
        }

        const currentTokenGeneration = tokenGeneration;

        const tokenRequest = requestPreviewToken(templateName)
            .then((tokenResponse) => {
                if (currentTokenGeneration !== tokenGeneration) {
                    return tokenResponse.token;
                }

                if (tokenRequestCache.get(templateName)?.promise !== tokenRequest) {
                    return tokenResponse.token;
                }

                storeToken(templateName, tokenResponse);

                return tokenResponse.token;
            })
            .finally(() => {
                if (tokenRequestCache.get(templateName)?.promise === tokenRequest) {
                    tokenRequestCache.delete(templateName);
                }
            });

        tokenRequestCache.set(templateName, {
            generation: currentTokenGeneration,
            promise: tokenRequest,
        });

        return tokenRequest;
    }

    async function requestPreviewToken(templateName) {
        const response = await fetch(PREVIEW_TOKEN_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                printess_template_name: templateName,
            }),
        });

        if (!response.ok) {
            throw await PricingRequestError.fromResponse(response, 'Token request failed');
        }

        const tokenResponse = await response.json();

        assertValidTokenResponse(tokenResponse);

        return tokenResponse;
    }

    async function requestPricingPreview(payload, token, signal) {
        const response = await fetch(PRICING_PREVIEW_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...payload,
                preview_token: token,
            }),
            signal,
        });

        if (!response.ok) {
            throw await PricingRequestError.fromResponse(response, 'Pricing preview request failed');
        }

        return response.json();
    }

    function assertValidTokenResponse(tokenResponse) {
        if (!tokenResponse || typeof tokenResponse !== 'object') {
            throw new Error('Invalid preview token response');
        }

        if (typeof tokenResponse.token !== 'string' || tokenResponse.token.trim() === '') {
            throw new Error('Preview token response is missing token');
        }

        if (!Number.isInteger(Number(tokenResponse.expires_at))) {
            throw new Error('Preview token response is missing expires_at');
        }
    }

    function storeToken(templateName, tokenResponse) {
        assertValidTokenResponse(tokenResponse);

        tokenCache.set(templateName, {
            token: tokenResponse.token,
            expiresAt: Number(tokenResponse.expires_at),
        });
    }

    function invalidateToken(templateName) {
        tokenGeneration += 1;
        tokenCache.delete(templateName);
        tokenRequestCache.delete(templateName);
    }

    function isTokenExpired(cachedToken) {
        const now = Math.floor(Date.now() / 1000);
        return cachedToken.expiresAt <= now + TOKEN_EXPIRY_BUFFER_SECONDS;
    }

    function resetRetryStateForPayloadChange(payloadSignature) {
        if (
            retryState &&
            !retryState.isGlobal &&
            retryState.payloadSignature !== payloadSignature
        ) {
            resetRetryState();
        }
    }

    function isPricingRequestAllowed(payloadSignature) {
        if (!retryState) {
            return true;
        }

        if (!retryState.isGlobal && retryState.payloadSignature !== payloadSignature) {
            return true;
        }

        return Date.now() >= retryState.nextAllowedRequestAt;
    }

    function registerPricingFailure(payloadSignature, error) {
        const status = error instanceof PricingRequestError ? error.status : null;
        const isGlobal = status === 429;
        const isSameFailureSeries = retryState && (
            (isGlobal && retryState.isGlobal) ||
            (!isGlobal &&
                !retryState.isGlobal &&
                retryState.payloadSignature === payloadSignature)
        );
        const failureCount = isSameFailureSeries
            ? retryState.failureCount + 1
            : 1;

        let nextAllowedRequestAt;

        if (status === 422) {
            nextAllowedRequestAt = Number.POSITIVE_INFINITY;
        } else if (status === 429 && Number.isFinite(error.retryAfterMs)) {
            nextAllowedRequestAt = Date.now() + error.retryAfterMs;
        } else {
            const backoffMs = Math.min(
                RETRY_BACKOFF_INITIAL_MS * (2 ** Math.min(failureCount - 1, 30)),
                RETRY_BACKOFF_MAX_MS
            );

            nextAllowedRequestAt = Date.now() + backoffMs;
        }

        retryState = {
            payloadSignature,
            nextAllowedRequestAt,
            failureCount,
            status,
            isGlobal,
        };
    }

    function resetRetryState() {
        retryState = null;
    }

    function createStablePayloadSignature(payload) {
        return JSON.stringify(sortObjectRecursively(payload));
    }

    function sortObjectRecursively(value) {
        if (Array.isArray(value)) {
            return value.map(sortObjectRecursively);
        }

        if (value && typeof value === 'object') {
            return Object.keys(value)
                .sort()
                .reduce((sorted, key) => {
                    sorted[key] = sortObjectRecursively(value[key]);
                    return sorted;
                }, {});
        }

        return value;
    }

    function getMemoizedPreviewResponse(payloadSignature) {
        const cachedItem = previewResponseCache.get(payloadSignature);

        if (!cachedItem) {
            return null;
        }

        if (Date.now() > cachedItem.expiresAt) {
            previewResponseCache.delete(payloadSignature);
            return null;
        }

        return cloneValue(cachedItem.response);
    }

    function memoizePreviewResponse(payloadSignature, response) {
        previewResponseCache.set(payloadSignature, {
            response: cloneValue(response),
            expiresAt: Date.now() + MEMOIZED_RESPONSE_TTL_MS,
        });

        if (previewResponseCache.size <= MAX_MEMOIZED_RESPONSES) {
            return;
        }

        const oldestKey = previewResponseCache.keys().next().value;

        if (oldestKey) {
            previewResponseCache.delete(oldestKey);
        }
    }

    function abortActivePreviewRequest() {
        if (activePreviewAbortController) {
            activePreviewAbortController.abort();
            activePreviewAbortController = null;
        }
    }

    function readTemplateName(context) {
        const productTemplateName = safeCall(context?.product, 'getTemplateName');

        if (typeof productTemplateName === 'string' && productTemplateName.trim() !== '') {
            return productTemplateName.trim();
        }

        const productTemplateProperty = context?.product?.templateName?.value || context?.product?.templateName;

        if (typeof productTemplateProperty === 'string' && productTemplateProperty.trim() !== '') {
            return productTemplateProperty.trim();
        }

        return null;
    }

    function normalizePriceRelevantFields(response) {
        return window.FacPrintessPricingFields?.normalizePriceRelevantFields(response) || {};
    }

    function safeApiCall(api, methodName, args = []) {
        try {
            const method = api?.[methodName];

            if (typeof method !== 'function') {
                debugLog('Printess API method is not available', methodName);
                return null;
            }

            return method.apply(api, args);
        } catch (error) {
            console.warn(`[CUSTOM_PRINTESS_PRICING] Printess API call failed: ${methodName}`);
            debugLog('Printess API call error details', error);
            return null;
        }
    }

    function safeCall(target, methodName) {
        try {
            if (!target || typeof target[methodName] !== 'function') {
                return null;
            }

            return target[methodName]();
        } catch (error) {
            console.warn(`[CUSTOM_PRINTESS_PRICING] Method call failed: ${methodName}`);
            debugLog('Method call error details', error);
            return null;
        }
    }

    function handlePricingLoading(payload) {
        lastUnavailableMessage = null;

        debugLog('Pricing preview loading', payload);

        window.dispatchEvent(new CustomEvent('fac:pricing-preview:loading', {
            detail: {
                printessTemplateName: payload.printess_template_name,
            },
        }));
    }

    function handlePricingSuccess(response, payloadSignature, fromCache) {
        lastUnavailableMessage = null;

        const safeResponse = cloneValue(response);

        debugLog('Pricing preview success', {
            response: safeResponse,
            payloadSignature,
            fromCache,
        });

        window.dispatchEvent(new CustomEvent('fac:pricing-preview:success', {
            detail: {
                response: safeResponse,
                payloadSignature,
                fromCache,
            },
        }));
    }

    function handlePricingError(error) {
        lastUnavailableMessage = null;

        const normalizedError = normalizePricingError(error);

        console.warn('[CUSTOM_PRINTESS_PRICING] Pricing preview failed', normalizedError.message);

        window.dispatchEvent(new CustomEvent('fac:pricing-preview:error', {
            detail: normalizedError,
        }));
    }

    function handlePricingUnavailable(error) {
        const message = error instanceof Error
            ? error.message
            : 'Pricing preview is currently unavailable';

        if (message === lastUnavailableMessage) {
            return;
        }

        lastUnavailableMessage = message;

        debugLog('Pricing preview currently unavailable', error);

        window.dispatchEvent(new CustomEvent('fac:pricing-preview:unavailable', {
            detail: {
                message,
            },
        }));
    }

    function normalizePricingError(error) {
        if (error instanceof PricingRequestError) {
            return {
                status: error.status,
                message: error.message,
                isValidationError: error.status === 422,
                isUnauthorized: error.status === 401,
                isRateLimited: error.status === 429,
                isServerError: error.status >= 500,
            };
        }

        return {
            status: null,
            message: error instanceof Error ? error.message : 'Unknown pricing preview error',
            isValidationError: false,
            isUnauthorized: false,
            isRateLimited: false,
            isServerError: false,
        };
    }

    function isAbortError(error) {
        return error?.name === 'AbortError';
    }

    function cloneValue(value) {
        if (typeof structuredClone === 'function') {
            return structuredClone(value);
        }

        return JSON.parse(JSON.stringify(value));
    }

    function cleanupPricingPreviewRuntime() {
        stopPolling();

        if (debounceTimer) {
            window.clearTimeout(debounceTimer);
            debounceTimer = null;
        }

        abortActivePreviewRequest();

        currentApi = null;
        currentContext = null;
        lastSuccessfulPayloadSignature = null;
        inFlightPayloadSignature = null;
        lastUnavailableMessage = null;
        resetRetryState();
        requestSequence += 1;
        tokenGeneration += 1;

        tokenRequestCache.clear();
        previewResponseCache.clear();
    }

    function debugLog(message, data) {
        if (!DEBUG_MODE) {
            return;
        }

        console.log(`[CUSTOM_PRINTESS_PRICING] ${message}`, data);
    }

    window.addEventListener('pagehide', cleanupPricingPreviewRuntime);

    class PricingRequestError extends Error {
        constructor(message, status, responseBody, retryAfterMs) {
            super(message);
            this.name = 'PricingRequestError';
            this.status = status;
            this.responseBody = responseBody;
            this.retryAfterMs = retryAfterMs;
        }

        static async fromResponse(response, fallbackMessage) {
            let responseBody = null;

            try {
                responseBody = await response.json();
            } catch (error) {
                responseBody = null;
            }

            const safeMessage = typeof responseBody?.message === 'string'
                ? responseBody.message
                : `${fallbackMessage} with status ${response.status}`;
            const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'));

            return new PricingRequestError(
                safeMessage,
                response.status,
                responseBody,
                retryAfterMs
            );
        }
    }

    function parseRetryAfterMs(retryAfterValue) {
        if (typeof retryAfterValue !== 'string' || retryAfterValue.trim() === '') {
            return null;
        }

        const normalizedValue = retryAfterValue.trim();

        if (/^\d+$/.test(normalizedValue)) {
            const retryAfterSeconds = Number(normalizedValue);
            return Number.isFinite(retryAfterSeconds)
                ? retryAfterSeconds * 1000
                : null;
        }

        const retryAfterDate = Date.parse(normalizedValue);

        if (!Number.isFinite(retryAfterDate)) {
            return null;
        }

        return Math.max(0, retryAfterDate - Date.now());
    }
})();
